/**
 * <delivery-availability> - #846 PDP delivery-stock correction.
 *
 * Shopify pools inventory across all locations, so a variant with stock only in
 * pickup-only stores reads "In stock" and Add-to-Cart is enabled even though the
 * warehouse can't ship it. This component fetches per-variant DELIVERABLE stock
 * (warehouse only) from the sportscraft-api Worker (`/apps/cx/inventory`) and corrects the
 * PDP surfaces to reflect shippable stock:
 *   - inventory messaging (`<variant-inventory>`): "Only N left" / In stock / Sold out
 *   - Add to Cart gate: disabled + "Sold out" when deliverable = 0 (unless oversell)
 *   - dynamic payment button hidden when sold out
 *   - Notify Me button revealed when sold out
 *
 * It is a front-end/messaging correction only - it does not change Shopify's stock,
 * so third-party apps and Klaviyo (native availability) are unaffected (see #517).
 *
 * Drop a marker <delivery-availability data-product-id="{{ product.id }}"> inside
 * .product-info (it scopes to the enclosing .product-info - no DOM wrap, so the
 * block-based product template is untouched). The variant picker uses the Section
 * Rendering API, so surfaces re-render from native state on every variant change -
 * we re-apply after each `variant:change`.
 */
class DeliveryAvailability extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    // No product (editor placeholder / empty state) → do nothing: never force a
    // Sold-Out treatment onto a blank marker.
    if (!this.productId) return;
    // Market signal - the worker reads the NZ delivery location(s) for 'NZ' and
    // the AU warehouse for everything else. Prefer the RUNTIME `Shopify.country`
    // (Shopify sets it fresh per request) over the server-rendered
    // `data-country-code`: the latter is baked from `localization.country` and can
    // be served stale (e.g. cached 'AU' on an /en-nz/ page), which would query the
    // wrong warehouse. Fall back to the attribute if the global is unavailable.
    var runtimeCountry = (window.Shopify && window.Shopify.country) || '';
    // Market the server rendered the flag / flag-map against (may be stale on a
    // cached page); the runtime market is authoritative for the live call.
    this.serverCountry = (this.dataset.countryCode || '').toUpperCase();
    this.countryCode = (runtimeCountry || this.dataset.countryCode || '').toUpperCase();
    // #846 flag-first gate, resolved server-side in product-info.liquid via the
    // shared sportscraft-delivery-purchasable helper:
    //   data-delivery-flag  - 'true'/'false'; the per-market Comestri flag
    //                         (rollout fallback to native availability applied).
    //   data-has-backorder  - 'true' when any variant has inventory_policy
    //                         'continue' (backorder items stay addable).
    this.flagFalse = this.dataset.deliveryFlag === 'false';
    this.hasBackorder = this.dataset.hasBackorder === 'true';
    // Per-variant flag map for the RUNTIME market. The server embeds both markets
    // ({ au:{id:state}, nz:{id:state} }); we pick the one matching this.countryCode
    // so a cross-market cached page can't pair runtime-market stock with the wrong
    // market's flags. Null when absent → _isDeliverable falls back to native.
    this.flagMap = this._parseFlagMap(this.countryCode);
    // { [legacyVariantId]: { deliverable_qty, inventory_policy } }
    this.map = null;
    this._onVariantChange = this._onVariantChange.bind(this);

    // Hide the availability region until the deliverable figure lands, so the
    // native "In stock" state never flashes before correction. Revealed in
    // _finish() whether the fetch succeeds (corrected) or fails (native fallback).
    this._root().classList.add('delivery-availability--pending');
    this._setSizePending(true);

    document.addEventListener('variant:change', this._onVariantChange);

    // Flag is the FIRST check: not deliverable and nothing backorderable means
    // no variant can ever be purchasable, so skip the live stock call entirely.
    // buy-buttons.liquid has already rendered Sold Out + Notify Me server-side;
    // this pass corrects the remaining JS-owned surfaces (inventory text, size
    // chips, payment button) without a network request.
    // Only trust the (server-rendered, stale-cacheable) flag to short-circuit
    // when it was computed for the shopper's actual runtime market - otherwise a
    // cached page from another market could force a false "Sold Out" that never
    // self-corrects. On mismatch, fall through to the live, runtime-market call.
    if (this.flagFalse && !this.hasBackorder && this.countryCode === this.serverCountry) {
      this.forcedUndeliverable = true;
      this._applyForced();
      this._finish();
      return;
    }

    this._load();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
    if (this._requestTimeout) clearTimeout(this._requestTimeout);
    if (this._requestController) this._requestController.abort();
  }

  // Scope root: this element is a lightweight marker inside .product-info (it does
  // not wrap the DOM, so the block-based product template stays untouched). All
  // surface queries + the first-paint gate run against the PDP product-info.
  _root() {
    return this._rootEl || (this._rootEl = this.closest('.product-info') || document.querySelector('.product-info') || document.body);
  }

  // Read the server-embedded flag map ({ au:{id:state}, nz:{id:state} }) and
  // return the sub-map for `market` ({ [legacyVariantId]: 'true'|'false'|'unset' })
  // or null if absent. Scoped to THIS product-info only (no document-level
  // fallback - that could return another product's map on a multi-product page).
  _parseFlagMap(market) {
    const el = this._root().querySelector('[data-sportscraft-variant-flags]');
    if (!el) return null;
    let parsed;
    try {
      parsed = JSON.parse(el.textContent || 'null');
    } catch (err) {
      console.warn('[delivery-availability] could not parse variant flag map:', err);
      return null;
    }
    if (!parsed) return null;
    const key = market === 'NZ' ? 'nz' : 'au';
    return parsed[key] || parsed.au || null;
  }

  async _load() {
    if (!this.productId) return this._finish();
    const base = window.SPORTSCRAFT_API_BASE;
    if (!base) {
      console.warn('[delivery-availability] SPORTSCRAFT_API_BASE not set — leaving native availability');
      return this._finish();
    }
    // Bound the wait so the pending treatment can never outlive a hung request.
    this._requestController = new AbortController();
    this._requestTimeout = setTimeout(() => this._requestController.abort(), 5000);
    try {
      const marketParam = this.countryCode ? `&country_code=${encodeURIComponent(this.countryCode)}` : '';
      const res = await fetch(`${base}/inventory?product_id=${encodeURIComponent(this.productId)}${marketParam}`, {
        headers: { Accept: 'application/json' },
        signal: this._requestController.signal,
      });
      if (!res.ok) throw new Error(`inventory ${res.status}`);
      const data = await res.json();
      this.map = data.variants || {};
      this.applyCorrection(this._currentVariantId());
      this._applySizeChips();
      this._applyNotifyDrawerChips();
    } catch (err) {
      // Degrade gracefully: reveal the native state unchanged rather than block.
      console.warn('[delivery-availability] fetch failed, using native availability:', err);
    } finally {
      clearTimeout(this._requestTimeout);
      this._requestTimeout = null;
      this._requestController = null;
      this._finish();
    }
  }

  _finish() {
    this._root().classList.remove('delivery-availability--pending');
    this._setSizePending(false);
  }

  // JAG #1000: on the main PDP, size chips must not advertise or allow selection
  // until the delivery-stock map has resolved. Other product-info contexts are
  // unchanged because the reported UX issue and acceptance criteria are PDP-only.
  _setSizePending(pending) {
    if (this.dataset.context !== 'main_product') return;
    const root = this._root();
    root.classList.toggle('delivery-availability--size-pending', pending);
    // Every chip group, matching the CSS descendant selector that dims them.
    root.querySelectorAll('[data-sportscraft-size-chips]').forEach((chips) => {
      chips.inert = pending;
      if (pending) {
        chips.setAttribute('aria-busy', 'true');
      } else {
        chips.removeAttribute('aria-busy');
      }
    });
  }

  // Selected variant id - source of truth is the product form's hidden id input,
  // which the theme keeps current across Section-Rendering variant swaps.
  _currentVariantId() {
    const input = this._root().querySelector('product-form input[name="id"]') || document.querySelector('product-form input[name="id"]');
    return input ? input.value : null;
  }

  _onVariantChange() {
    if (!this.map && !this.forcedUndeliverable) return;
    // Let the section swap settle, then re-apply against the new DOM. The size
    // picker re-renders from native state via the Section Rendering API on every
    // variant change, so the per-chip correction must re-run alongside the
    // selected-variant surfaces.
    requestAnimationFrame(() => {
      if (this.forcedUndeliverable) {
        this._applyForced();
        return;
      }
      this.applyCorrection(this._currentVariantId());
      this._applySizeChips();
    });
  }

  // Flag-false + no backorder: every variant is undeliverable by definition -
  // apply the sold-out treatment to all JS-owned surfaces without any map.
  _applyForced() {
    this._applyInventory(0, false, false);
    this._applyBuyButtons(false);
    this._applySizeChips();
    this._applyNotifyDrawerChips();
  }

  // Per-variant purchasability, resolved from THIS variant's own flag (not the
  // product-level one). Order: backorder is always addable; else a per-variant
  // flag of 'false' means "not deliverable" regardless of warehouse quantity;
  // else (flag 'true' or unset) warehouse quantity drives it. The per-variant
  // flag map is embedded server-side (data-sportscraft-variant-flags) so a product with
  // mixed variant flags corrects each variant to its own state - the worker
  // returns quantity only, so without this a flag-false variant with stock would
  // wrongly read "In stock".
  _isDeliverable(qty, oversell, variantId) {
    if (oversell) return true;
    if (this.flagMap) {
      if (this.flagMap[String(variantId)] === 'false') return false;
      return qty > 0;
    }
    // No per-variant map (defensive): fall back to the product-level flag.
    if (this.flagFalse) return false;
    return qty > 0;
  }

  applyCorrection(variantId) {
    if (!this.map || !variantId) return;
    const entry = this.map[String(variantId)];
    if (!entry) return; // unknown variant - leave native

    const qty = entry.deliverable_qty || 0;
    const oversell = entry.inventory_policy === 'continue';
    const deliverable = this._isDeliverable(qty, oversell, variantId);

    this._applyInventory(qty, deliverable, oversell);
    this._applyBuyButtons(deliverable);
  }

  // Rewrite <variant-inventory> text + status colour from deliverable stock.
  _applyInventory(qty, deliverable, oversell) {
    const el = this._root().querySelector('variant-inventory');
    if (!el) return;
    const textEl = el.querySelector('.inventory__text');
    if (!textEl) return;

    // Locale strings + low threshold are exposed by inventory.liquid as data-*.
    const s = el.dataset;
    const lowThreshold = parseInt(s.lowThreshold || '0', 10);
    let status, text;

    if (!deliverable) {
      status = 'error';
      text = s.outOfStock || 'Out of stock';
    } else if (oversell && qty <= 0) {
      status = 'warning';
      text = s.oversellStock || 'Available';
    } else if (lowThreshold > 0 && qty > 0 && qty <= lowThreshold) {
      status = 'warning';
      // The %%N%%-templated string is rendered with a placeholder `count`, so
      // Liquid always resolves the plural ("units") form. inventory.liquid also
      // exposes the singular form (rendered with count: 1) so qty 1 reads
      // "Only 1 unit left", not "Only 1 units left".
      text = qty === 1 && s.lowStockWithCountOne
        ? s.lowStockWithCountOne
        : (s.lowStockWithCount || 'Only %%N%% left').replace('%%N%%', qty);
    } else if (s.showQuantity === 'true' && qty > 0) {
      status = 'success';
      text = qty === 1 && s.inStockWithCountOne
        ? s.inStockWithCountOne
        : (s.inStockWithCount || '%%N%% in stock').replace('%%N%%', qty);
    } else {
      status = 'success';
      text = s.inStock || 'In stock';
    }

    // Warning-only mode (data-low-stock-only, from the block's sportscraft_low_stock_only):
    // the drawn PDP types only the low-stock count line - in-stock and sold-out
    // corrections blank + hide the element instead of surfacing undrawn copy
    // (sold-out surfaces belong to sportscraft-auto-stock-message and the buy buttons).
    const lowStockOnly = s.lowStockOnly === 'true';
    const isLowStockText = status === 'warning' && lowThreshold > 0 && qty > 0 && qty <= lowThreshold && deliverable;
    if (lowStockOnly && !isLowStockText) text = '';

    textEl.textContent = text;
    el.classList.remove('text-success', 'text-warning', 'text-error');
    el.classList.add(`text-${status}`);
    // If the variant-inventory was suppressed at render (no native text), ensure
    // visible - unless warning-only just blanked it for this variant.
    el.hidden = lowStockOnly && !isLowStockText;
  }

  // Gate Add to Cart + payment button + Notify Me on deliverable stock.
  _applyBuyButtons(deliverable) {
    const buy = this._root().querySelector('buy-buttons');
    if (!buy) return;

    const atc = buy.querySelector('button[type="submit"]');
    if (atc) {
      const label = atc.querySelector('.sportscraft-button__label, .button__label') || atc;
      if (!deliverable) {
        atc.disabled = true;
        atc.setAttribute('aria-disabled', 'true');
        if (this.dataset.soldOutLabel) label.textContent = this.dataset.soldOutLabel;
      } else if (atc.disabled && atc.dataset.gcGuardDisabled !== 'true') {
        // A button the gift-card cart guard disabled is the guard's to re-enable.
        // Re-enable ONLY a server-DISABLED ATC. buy-buttons.liquid renders from
        // the SERVER market (stale-cacheable); if that market said not-deliverable
        // it rendered a disabled "Sold out" button, so on a cross-market cached
        // page a runtime-deliverable variant would otherwise stay falsely Sold Out.
        // Guarding on `atc.disabled` means we never touch an already-enabled button
        // - so a bespoke native label (e.g. "Pre-order") is left intact.
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
        if (this.dataset.addLabel) label.textContent = this.dataset.addLabel;
      }
    }

    // Hide the dynamic payment button when not deliverable - UNLESS the gift-card
    // cart guard is deliberately hiding it (cart already holds a gift card). The
    // guard sets data-gc-guard-hidden on this same element and owns its visibility
    // while that flag is set; writing style.display here on a variant:change would
    // silently re-expose the ShopPay/PayPal express-checkout button the guard hid,
    // re-opening the one-gift-card-per-order bypass (#762 × #846 P1 - the express
    // button doesn't route through the guard's submit-capture). Mirror the guard's
    // "only touch what we own" rule; when the guard un-hides it clears the flag and
    // the next variant:change restores normal deliverability control.
    const payment = buy.querySelector('.shopify-payment-button');
    if (payment && payment.dataset.gcGuardHidden !== 'true') {
      payment.style.display = deliverable ? '' : 'none';
    }

    // Reveal the Notify Me button (rendered but may be hidden) when sold out.
    const notify = buy.querySelector('.sportscraft-notify-me');
    if (notify) notify.hidden = deliverable;
  }

  // Correct each size chip to deliverable stock (#846). A variant that reads
  // native-available but has 0 warehouse stock must flip to the sold-out /
  // Notify Me chip; low-stock must count deliverable units, not pooled ones.
  // Chips already native-sold-out are left as rendered (native is correct).
  // Mirrors sportscraft-size-chip.liquid's Liquid branches so the corrected chip is
  // visually and behaviourally identical to a server-rendered sold-out chip.
  _applySizeChips() {
    if (!this.map && !this.forcedUndeliverable) return;
    const wrap = this._root().querySelector('[data-sportscraft-size-chips]');
    if (!wrap) return;

    const d = wrap.dataset;
    const lowThreshold = parseInt(d.lowThreshold || '0', 10);
    const drawerId = d.drawerId || '';
    const disableNotify = d.lifecycle === 'Disable Notify Me';

    wrap.querySelectorAll('.sportscraft-size-chip[data-variant-id]').forEach((chip) => {
      // Only correct chips native Shopify thinks are available; a chip already
      // rendered sold-out is correct (0 anywhere means 0 at the warehouse too).
      if (chip.getAttribute('data-native-available') !== 'true') return;

      // Forced mode (flag false, nothing backorderable): every native-available
      // chip is undeliverable by definition - no per-variant map exists.
      if (this.forcedUndeliverable) {
        this._chipSoldOut(chip, d, drawerId, disableNotify);
        return;
      }

      const chipVariantId = chip.getAttribute('data-variant-id');
      const entry = this.map[String(chipVariantId)];
      if (!entry) return; // unknown variant - leave native

      const qty = entry.deliverable_qty || 0;
      const oversell = entry.inventory_policy === 'continue';
      const deliverable = this._isDeliverable(qty, oversell, chipVariantId);
      const status = this._chipStatusEl(chip);

      if (!deliverable) {
        this._chipSoldOut(chip, d, drawerId, disableNotify);
      } else if (!oversell && lowThreshold > 0 && qty > 0 && qty <= lowThreshold) {
        // Low on deliverable stock (native message may reflect pooled qty).
        chip.classList.remove('sportscraft-size-chip--oos', 'sportscraft-size-chip--disabled', 'sportscraft-size-chip--open-drawer');
        chip.removeAttribute('data-notify-drawer');
        chip.classList.add('sportscraft-size-chip--low');
        status.textContent = qty === 1 ? d.msgOnlyOne || '' : d.msgLow || '';
      } else {
        // Genuinely deliverable above threshold → clear any native low-stock /
        // sold-out styling that was derived from pooled inventory.
        chip.classList.remove('sportscraft-size-chip--oos', 'sportscraft-size-chip--disabled', 'sportscraft-size-chip--open-drawer', 'sportscraft-size-chip--low');
        chip.removeAttribute('data-notify-drawer');
        status.textContent = '';
      }
    });
  }

  // Sportscraft #290 x #846: the Notify Me drawer's size chips render EVERY variant -
  // native-sold-out ones visible, native-available ones HIDDEN with their radio
  // disabled. Reveal + enable the native-available ones this variant map says are
  // deliverable-OUT (0 warehouse stock), so a shopper can register for a size that
  // is sold out at the warehouse even though Shopify still reads it available.
  // Genuinely deliverable sizes stay hidden + disabled, so nobody can register a
  // notify for an in-stock size. The chips live in the drawer, OUTSIDE this
  // component's root, so match them by product id across the whole document.
  _applyNotifyDrawerChips() {
    if (!this.map && !this.forcedUndeliverable) return;
    const productId = this.productId;
    if (!productId) return;
    document
      .querySelectorAll(`[data-sportscraft-nm-size-chips][data-product-id="${productId}"]`)
      .forEach((wrap) => {
        const d = wrap.dataset;
        const disableNotify = d.lifecycle === 'Disable Notify Me';
        wrap.querySelectorAll('.sportscraft-size-chip[data-variant-id]').forEach((chip) => {
          // Native-sold-out chips are already shown + labelled server-side.
          if (chip.getAttribute('data-native-available') !== 'true') return;
          const forId = chip.getAttribute('for');
          const radio = forId ? document.getElementById(forId) : null;

          let deliverable = true;
          if (this.forcedUndeliverable) {
            deliverable = false;
          } else {
            const entry = this.map[String(chip.getAttribute('data-variant-id'))];
            // Unknown variant → treat as deliverable (leave hidden), never reveal on a guess.
            deliverable = entry
              ? this._isDeliverable(entry.deliverable_qty || 0, entry.inventory_policy === 'continue', chip.getAttribute('data-variant-id'))
              : true;
          }

          if (!deliverable && !disableNotify) {
            // Deliverable-out → notify-eligible: reveal + enable. No extra class: the
            // drawer's own sold-out chips carry no --oos modifier (that one strikes the
            // value through, which this drawer's design does not use), so a revealed
            // chip must look exactly like a server-rendered one.
            chip.hidden = false;
            this._chipStatusEl(chip).textContent = d.msgNotify || '';
            if (radio) radio.disabled = false;
          } else {
            // Deliverable (or Notify Me disabled) → keep it out of the form.
            chip.hidden = true;
            if (radio) {
              radio.disabled = true;
              if (radio.checked) radio.checked = false;
            }
          }
        });
      });
  }

  // Deliverable sold out despite native availability → match the server-rendered
  // OOS chip exactly (classes, Notify Me drawer wiring, status message).
  _chipSoldOut(chip, d, drawerId, disableNotify) {
    const status = this._chipStatusEl(chip);
    chip.classList.add('sportscraft-size-chip--oos', 'sportscraft-size-chip--disabled');
    chip.classList.remove('sportscraft-size-chip--low');
    if (disableNotify || !drawerId) {
      status.textContent = disableNotify ? d.msgSoldOut || '' : d.msgNotify || '';
    } else {
      status.textContent = d.msgNotify || '';
      chip.classList.add('sportscraft-size-chip--open-drawer');
      chip.setAttribute('data-notify-drawer', drawerId);
    }
  }

  // The status span is only server-rendered when the chip carries a message; a
  // native in-stock chip corrected down to low-stock / sold-out needs one made.
  _chipStatusEl(chip) {
    let s = chip.querySelector('.sportscraft-size-chip__status');
    if (!s) {
      s = document.createElement('span');
      s.className = 'sportscraft-size-chip__status';
      chip.appendChild(s);
    }
    return s;
  }
}

if (!customElements.get('delivery-availability')) {
  customElements.define('delivery-availability', DeliveryAvailability);
}
